import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Header";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { AdminAccessState } from "@/components/AdminAccessState";
import {
  Eye, RefreshCw, Download, Search,
  Users, CheckCircle2, PauseCircle, XCircle,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { useTimezone } from '@/contexts/TimezoneContext';

/* ===================== TYPES ===================== */

interface PlatformUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  phone_number: string;
  timezone: string;
  avatar: string | null;
  plan: number;
  plan_name: string;
  created_at?: string | null;
  date_joined?: string | null;
  last_login?: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  plan_expired?: string | null;
  get_remaining_campaigns?: number | null;
  is_in_free_trial?: boolean;
  has_used_free_trial?: boolean;
  pause_count: number;
  extra_hubs_credit?: number | null;
  active_addons: number[];
  status: "Active" | "Paused" | "Inactive" | "Suspended";
}

interface AdminUserStats {
  total: number;
  active: number;
  paused: number;
  inactive: number;
}

/* ===================== HELPERS ===================== */

const normalizeStatus = (status?: string) =>
  status === "Suspended" || status === "inactive" ? "Inactive" : status || "Unknown";

const getPlanStyle = (plan: string) => {
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    "Pro": { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-400" },
    "Pro+": { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-400" },
    "Starter": { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-400" },
    "Pro Annual": { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-400" },
    "Pro+ Annual": { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-400" },
    "Starter Annual": { bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-400" },
    "One-Time": { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-400" },
  };
  return map[plan] || { bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-400" };
};

const getStatusStyle = (status: string) => {
  const s = normalizeStatus(status);
  const map: Record<string, { dot: string; text: string; border: string }> = {
    Active: { dot: "bg-emerald-500", text: "text-emerald-700", border: "border-l-emerald-400" },
    Paused: { dot: "bg-amber-400", text: "text-amber-700", border: "border-l-amber-400" },
    Inactive: { dot: "bg-red-400", text: "text-red-600", border: "border-l-red-400" },
  };
  return map[s] || { dot: "bg-gray-400", text: "text-gray-500", border: "border-l-gray-300" };
};

const AVATAR_COLORS = [
  { bg: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-violet-100", text: "text-violet-700" },
  { bg: "bg-emerald-100", text: "text-emerald-700" },
  { bg: "bg-rose-100", text: "text-rose-700" },
  { bg: "bg-amber-100", text: "text-amber-700" },
  { bg: "bg-cyan-100", text: "text-cyan-700" },
];
const avatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

/* ===================== SKELETON ===================== */

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);


const SkeletonRow = () => (
  <tr className="border-b border-gray-100">
    <td className="px-5 py-4 border-l-2 border-l-gray-200">
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
        <Skeleton className="h-3 w-24" />
      </div>
    </td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-40" /></td>
    <td className="px-5 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-8" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-32" /></td>
    <td className="px-5 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
    <td className="px-5 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-8" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-32" /></td>
    <td className="px-5 py-4"><Skeleton className="h-3 w-32" /></td>
  </tr>
);

/* ===================== STAT CARD ===================== */

const StatCard = ({
  label, value, icon, valueClass, loading,
}: {
  label: string; value: number; icon: React.ReactNode;
  valueClass: string; loading: boolean;
}) => (
  <div className="bg-white rounded-2xl border border-gray-300 px-5 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="p-2.5 rounded-xl bg-gray-50 flex-shrink-0">{icon}</div>
    <div>
      <p className="text-xs text-gray-400 mb-1 font-medium">{label}</p>
      {loading
        ? <Skeleton className="h-7 w-12" />
        : <p className={`text-2xl font-bold leading-none ${valueClass}`}>{value}</p>
      }
    </div>
  </div>
);

/* ===================== COMPONENT ===================== */

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const toggleMobileRef = useRef<(() => void) | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { status: authStatus, retrying, retry } = useAdminAuth();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<AdminUserStats>({ total: 0, active: 0, paused: 0, inactive: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilterField, setDateFilterField] = useState<"joined" | "last_login">("joined");
  const [dateFilterRange, setDateFilterRange] = useState<
    "24_hours" | "1_week" | "1_month" | "6_months" | "1_year" | "all_time"
  >("all_time");
  const isFirstLoad = useRef(true);
  const [filtering, setFiltering] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const isRefreshTriggered = useRef(false);
  const refreshStartTime = useRef<number | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── fetch users ── */
  useEffect(() => {
    if (authStatus !== "admin") return;
    const isInitial = isFirstLoad.current;
    if (isInitial) setLoading(true);
    else if (isRefreshTriggered.current) setRefreshing(true);
    else setFiltering(true);

    const controller = new AbortController();

    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (planFilter !== "all") params.set("plan_name", planFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    params.set("filter_by", dateFilterField);
    params.set("filter_range", dateFilterRange);

    axiosInstance.get(`${endpoints.userprofiles}?${params.toString()}`, { signal: controller.signal })
      .then(res => {
        if (Array.isArray(res.data)) {
          setUsers(res.data);
          setTotalCount(res.data.length);
          setStats({
            total: res.data.length,
            active: res.data.filter((u: PlatformUser) => normalizeStatus(u.status) === "Active").length,
            paused: res.data.filter((u: PlatformUser) => normalizeStatus(u.status) === "Paused").length,
            inactive: res.data.filter((u: PlatformUser) => normalizeStatus(u.status) === "Inactive").length,
          });
        } else if (res.data?.results) {
          setUsers(res.data.results);
          setTotalCount(res.data.count || res.data.results.length || 0);
          setStats({
            total: res.data.stats?.total ?? res.data.count ?? res.data.results.length ?? 0,
            active: res.data.stats?.active ?? 0,
            paused: res.data.stats?.paused ?? 0,
            inactive: res.data.stats?.inactive ?? res.data.stats?.suspended ?? 0,
          });
        } else {
          setUsers(res.data || []);
          setTotalCount(res.data?.length || 0);
          setStats({ total: res.data?.length || 0, active: 0, paused: 0, inactive: 0 });
        }
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setUsers([]); setTotalCount(0);
        setStats({ total: 0, active: 0, paused: 0, inactive: 0 });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setFiltering(false);
        if (isRefreshTriggered.current && refreshStartTime.current !== null) {
          const elapsed = Date.now() - refreshStartTime.current;
          const minSpinMs = 1200;
          const remaining = Math.max(0, minSpinMs - elapsed);
          if (remaining > 0) {
            refreshTimeoutRef.current = window.setTimeout(() => {
              setRefreshing(false);
              refreshTimeoutRef.current = null;
              refreshStartTime.current = null;
            }, remaining);
          } else {
            setRefreshing(false);
            refreshStartTime.current = null;
          }
        } else {
          setRefreshing(false);
          refreshStartTime.current = null;
        }
        isRefreshTriggered.current = false;
        isFirstLoad.current = false;
      });

    return () => {
      controller.abort();
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [authStatus, page, pageSize, searchTerm, planFilter, statusFilter, dateFilterField, dateFilterRange, refreshKey]);

  /* ── pagination ── */
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const getPaginationItems = (current: number, total: number): Array<number | "..."> => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const items: Array<number | "..."> = [];
    const ws = Math.max(2, current - 1);
    const we = Math.min(total - 1, current + 1);
    items.push(1);
    if (ws > 2) items.push("...");
    for (let p = ws; p <= we; p++) items.push(p);
    if (we < total - 1) items.push("...");
    items.push(total);
    return items;
  };

  const hasActiveFilters =
    searchTerm.trim().length > 0 || planFilter !== "all" ||
    statusFilter !== "all" || dateFilterField !== "joined" || dateFilterRange !== "all_time";

  if (authStatus === "loading") {
    return <AdminUsersSkeleton />;
  }

  // if (authStatus === "unavailable") {
  //   return (
  //     <AdminAccessState
  //       pageTitle="User Management"
  //       variant="unavailable"
  //       onRetry={retry}
  //       retrying={retrying}
  //     />
  //   );
  // }

  // if (authStatus === "denied") {
  //   return (
  //     <AdminAccessState
  //       pageTitle="User Management"
  //       variant="denied"
  //     />
  //   );
  // }

  const COLS = [
    { label: "Name", width: 220 },
    { label: "Email", width: 230 },
    { label: "Plan", width: 140 },
    { label: "Remaining Launch", width: 150 },
    { label: "Plan Expires", width: 180 },
    { label: "Trial Active", width: 110 },
    { label: "Status", width: 110 },
    { label: "Extra Hub Credits", width: 150 },
    { label: "Joined", width: 190 },
    { label: "Last Login", width: 190 },
  ];

  /* ===================== RENDER ===================== */
  return (
    <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
      <Sidebar registerMobileToggle={(fn) => { toggleMobileRef.current = fn; }} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <PageHeader
          pageTitle="User Management"
          onMenuClick={() => toggleMobileRef.current?.()}
        />

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">

            {/* ── HEADER ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">User Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">Manage platform users and subscriptions</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 gap-2 text-xs font-medium border-gray-200 hover:bg-gray-50"
                  onClick={() => {
                    if (refreshing) return;
                    isRefreshTriggered.current = true;
                    refreshStartTime.current = Date.now();
                    if (refreshTimeoutRef.current) {
                      window.clearTimeout(refreshTimeoutRef.current);
                      refreshTimeoutRef.current = null;
                    }
                    setRefreshing(true);
                    setRefreshKey((k) => k + 1);
                  }}
                  disabled={refreshing}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </Button>
                {/* <Button
                  variant="outline"
                  size="sm"
                  className="h-9 gap-2 text-xs font-medium border-gray-200 hover:bg-gray-50"
                >
                  <Download className="w-3.5 h-3.5" /> Export
                </Button> */}
              </div>
            </div>

            {/* ── STATS ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Users" value={stats.total || totalCount} loading={loading}
                valueClass="text-gray-900"
                icon={<Users className="w-5 h-5 text-gray-500" />} />
              <StatCard label="Active" value={stats.active} loading={loading}
                valueClass="text-emerald-600"
                icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />} />
              <StatCard label="Paused" value={stats.paused} loading={loading}
                valueClass="text-amber-600"
                icon={<PauseCircle className="w-5 h-5 text-amber-500" />} />
              <StatCard label="Inactive" value={stats.inactive} loading={loading}
                valueClass="text-red-500"
                icon={<XCircle className="w-5 h-5 text-red-400" />} />
            </div>

            {/* ── FILTERS ── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
              <div className="flex flex-col gap-3">

                {/* Search — full width always */}
                <div className="relative w-full">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10 h-9 text-sm border-gray-200 bg-gray-50 rounded-xl focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all w-full"
                  />
                </div>

                {/* Dropdowns — 2x2 grid on mobile, single row on desktop */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Select value={dateFilterField} onValueChange={(v) => { setDateFilterField(v as any); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="joined">Date joined</SelectItem>
                      <SelectItem value="last_login">Last login</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={dateFilterRange} onValueChange={(v) => { setDateFilterRange(v as any); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24_hours">Last 24 hours</SelectItem>
                      <SelectItem value="1_week">Last 1 week</SelectItem>
                      <SelectItem value="1_month">Last 1 month</SelectItem>
                      <SelectItem value="6_months">Last 6 months</SelectItem>
                      <SelectItem value="1_year">Last 1 year</SelectItem>
                      <SelectItem value="all_time">All time</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue placeholder="All Plans" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="One-Time">One-Time</SelectItem>
                      <SelectItem value="Starter">Starter</SelectItem>
                      <SelectItem value="Pro">Pro</SelectItem>
                      <SelectItem value="Pro+">Pro+</SelectItem>
                      <SelectItem value="Starter Annual Plan">Starter Annual</SelectItem>
                      <SelectItem value="Pro Annual Plan">Pro Annual</SelectItem>
                      <SelectItem value="Pro+ Annual Plan">Pro+ Annual</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="h-9 text-sm border-gray-200 bg-gray-50 rounded-xl w-full">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Paused">Paused</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

              </div>
            </div>

            {/* ── TABLE CARD ── */}
            <div className="relative">
              <div
                className={`overflow-x-auto transition-opacity duration-200 ${filtering ? "opacity-40" : "opacity-100"}`}
                style={{ transform: "rotateX(180deg)" }}
              >
                <table
                  className="border-l border-r border-gray-300"
                  style={{
                    minWidth: `${COLS.reduce((a, c) => a + c.width, 0)}px`,
                    borderCollapse: "collapse",
                    width: "100%",
                    transform: "rotateX(180deg)",
                  }}
                >

                  <colgroup>
                    {COLS.map((c, i) => <col key={i} style={{ width: c.width }} />)}
                  </colgroup>

                  <thead>
                    <tr className="border-t border-b border-gray-300 bg-gray-50/80">
                      {COLS.map((c) => (
                        <th
                          key={c.label}
                          className="px-5 py-3.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap"
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={COLS.length} className="py-20 text-center">
                          <div className="flex flex-col items-center gap-3 text-gray-400">
                            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                              <Users className="w-7 h-7 opacity-40" />
                            </div>
                            <p className="text-sm font-medium">
                              {hasActiveFilters ? "No users match current filters" : "No users found"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => {
                        const st = getStatusStyle(user.status);
                        const pl = getPlanStyle(user.plan_name || "No Plan");
                        const av = avatarColor(user.id);
                        return (
                          <tr
                            key={user.id}
                            className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors group border-l-[3px] ${st.border}`}
                          >
                            {/* Name */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9 flex-shrink-0">
                                  {user.avatar
                                    ? <AvatarImage src={user.avatar} />
                                    : <AvatarFallback className={`text-xs font-semibold ${av.bg} ${av.text}`}>
                                      {user.first_name[0]?.toUpperCase() || "?"}{user.last_name[0]?.toUpperCase() || "?"}
                                    </AvatarFallback>
                                  }
                                </Avatar>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {user.first_name} {user.last_name}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Email */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">{user.email}</span>
                            </td>

                            {/* Plan */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${pl.bg} ${pl.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${pl.dot}`} />
                                {user.plan_name || "No Plan"}
                              </span>
                            </td>

                            {/* Remaining Launch */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm font-semibold text-gray-800 tabular-nums">
                                {user.get_remaining_campaigns ?? 0}
                              </span>
                            </td>

                            {/* Plan Expires */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">
                                {formatDateTime(user.plan_expired) || "—"}
                              </span>
                            </td>

                            {/* Trial Active */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              {user.is_in_free_trial ? (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 leading-none">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 leading-none">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                  No
                                </span>
                              )}
                            </td>

                            {/* Status */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold`}>
                                <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                                <span className={st.text}>{normalizeStatus(user.status)}</span>
                              </span>
                            </td>

                            {/* Extra Hub Credits */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm font-semibold text-gray-800 tabular-nums">
                                {user.extra_hubs_credit ?? 0}
                              </span>
                            </td>

                            {/* Joined */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">
                                {formatDateTime(user.date_joined ?? user.created_at) || "—"}
                              </span>
                            </td>

                            {/* Last Login */}
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="text-sm text-gray-500">
                                {formatDateTime(user.last_login) || "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── PAGINATION ── */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 font-medium">
                  Page {page} of {totalPages} · <span className="text-gray-600">{users.length} users</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={loading || page <= 1}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {getPaginationItems(page, totalPages).map((item, idx) =>
                    item === "..." ? (
                      <span key={`e-${idx}`} className="w-8 text-center text-xs text-gray-400">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item as number)}
                        disabled={loading}
                        className={`h-8 w-8 rounded-lg text-xs font-semibold transition-colors ${item === page
                          ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={loading || page >= totalPages}
                    className="h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;

export const AdminUsersSkeleton: React.FC = () => (
  <div className="flex h-screen overflow-hidden bg-[#f8f9fb]">
    <Sidebar />
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      <PageHeader pageTitle="User Management" />
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-7 py-6 space-y-6 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Skeleton className="h-24" /><Skeleton className="h-24" />
            <Skeleton className="h-24" /><Skeleton className="h-24" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{ transform: "rotateX(180deg)" }}>
              <table className="min-w-full w-full border-collapse" style={{ transform: "rotateX(180deg)", minWidth: "1680px" }}>
                <thead className="border-b border-gray-100 bg-gray-50/80">
                  <tr>
                    {Array.from({ length: 10 }).map((_, idx) => (
                      <th key={idx} className="px-5 py-3.5 text-left">
                        <Skeleton className="h-3 w-24" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, idx) => <SkeletonRow key={idx} />)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
